import { onlineVisitFactorUrl } from '@/common/utils/onlineVisitFactorUrl';
import { useSearch } from '@/common/apis/services/search/search';
import Button from '@/common/components/atom/button';
import useCustomize from '@/common/hooks/useCustomize';
import buildSubstituteDoctorSearch, { SUBSTITUTE_DOCTOR_SEARCH_REF, buildSubstituteDoctorSearchText } from '@/common/utils/buildSubstituteDoctorSearch';
import { buildInPersonLine, resolveInPersonCity } from '@/common/utils/substituteInPersonLine';
import { splunkInstance } from '@/common/services/splunk';
import { useSearchRouting } from '@/modules/search/hooks/useSearchRouting';
import { SubstituteDoctorSuggestion } from './SubstituteDoctorSuggestion';
import random from 'lodash/random';
import { useEffect, useMemo, useRef } from 'react';

interface SubstituteDoctorsInlineProps {
  displayName: string;
  expertises: any;
  doctorCity?: string;
  /** City for the in-person line only: the doctor's own city, else their first clinic's.
   *  Kept separate from `doctorCity` so the online-visit search keeps its nationwide
   *  fallback when a doctor has no city (review 2026-09-20). */
  inPersonCity?: string;
  id?: string;
}

/**
 * Replacement of the removed auto-popup (owner 2026-10-12): the substitute-doctor suggestion renders
 * inline under the "no booking" alert instead of interrupting the visitor ~6s after arrival — ~2/3
 * of Google arrivals on no-booking profiles saw the popup and ~98.5% just closed it; they came for
 * THIS doctor, so NoBookingContact (the doctor's own phone/address) renders above this block.
 */
const SubstituteDoctorsInline = ({
  displayName,
  expertises,
  doctorCity,
  inPersonCity,
  id = 'substitute-doctors-inline',
}: SubstituteDoctorsInlineProps) => {
  const customize = useCustomize(state => state.customize);
  const searchData = useSearch(buildSubstituteDoctorSearch(expertises?.expertises, doctorCity), {
    enabled: !customize.partnerKey,
  });
  const inPersonText = buildSubstituteDoctorSearchText(expertises?.expertises);
  // The specialty's city page (/s/<city>/<group>/) pins the city in the path. A free-text search on
  // /s/<city>/?text= lets the visitor's saved city override it (seen 2026-09-19: Isfahan link showed
  // Tehran with "no results"). Text search stays only as the fallback when the group has no slug.
  const groupSlug: string | undefined = expertises?.group_expertises?.[0]?.en_slug;
  // Only the in-person line narrows to a clinic city; `doctorCity` stays as the profile sent it so
  // `buildSubstituteDoctorSearch` keeps falling back to 'ir' (nationwide) for a doctor with no city.
  const inPersonCityResolved = resolveInPersonCity(inPersonCity, doctorCity);
  const inPersonRoute = decodeURIComponent(inPersonCityResolved || '') + (groupSlug ? `/${groupSlug}` : '');
  const inPersonSearch = useSearch(
    {
      route: inPersonRoute,
      query: groupSlug ? { ref: SUBSTITUTE_DOCTOR_SEARCH_REF } : { ref: SUBSTITUTE_DOCTOR_SEARCH_REF, text: inPersonText },
    },
    { enabled: !customize.partnerKey && !!inPersonCityResolved && (!!groupSlug || !!inPersonText) },
  );
  const inPersonLine = useMemo(
    () => buildInPersonLine(inPersonSearch.data?.search?.result, displayName),
    [inPersonSearch.data, displayName],
  );
  const groupName = expertises?.group_expertises?.[0]?.name;
  const { changeRoute } = useSearchRouting();

  const handleClickDoctorCard = ({ doctor, element }: { doctor: any; element: any }) => {
    const url: string = doctor.url ?? '';
    splunkInstance('search').sendEvent({
      group: 'profile_visit_online_doctor_recommendation',
      type: 'profile_visit_online_doctor_recommendation-click-doctor-card',
      event: {
        slug: url.replace('/dr/', ''),
        variant: 'online-first',
      },
    });
    return location.assign(onlineVisitFactorUrl(doctor));
  };

  const handleClickInPersonLine = () => {
    splunkInstance('search').sendEvent({
      group: 'profile_visit_online_doctor_recommendation',
      type: 'profile_visit_online_doctor_recommendation-click-inperson-line',
      event: {
        slug: substituteDoctor?.url?.replace('/dr/', ''),
        city: inPersonCityResolved,
        count: inPersonLine?.count,
      },
    });
    location.assign(
      groupSlug
        ? `/s/${inPersonCityResolved}/${groupSlug}/`
        : `/s/${inPersonCityResolved}/?text=${encodeURIComponent(inPersonText)}`,
    );
  };

  const handleClickMoreDoctors = () => {
    splunkInstance('search').sendEvent({
      group: 'profile_visit_online_doctor_recommendation',
      type: 'profile_visit_online_doctor_recommendation-click-more-doctors',
      event: {
        slug: substituteDoctor?.url?.replace('/dr/', ''),
        variant: 'online-first',
      },
    });
    changeRoute({
      query: {
        turn_type: 'consult',
        text: expertises?.expertises?.map((expertise: any) => expertise.expertise_name)[0],
      },
      params: {
        city: 'ir',
      },
      previousQueries: false,
    });
  };

  const substituteDoctor = useMemo(() => searchData.data?.search?.result?.[random(0, 2)] ?? {}, [searchData.data]);

  // The popup's `-open-modal` counted views when it interrupted the visitor; inline, the render IS
  // the view. Sent once per picked substitute so re-renders never double-count the denominator.
  const viewSentFor = useRef<string | null>(null);
  useEffect(() => {
    const slug = substituteDoctor?.url?.replace('/dr/', '');
    if (!slug || viewSentFor.current === slug) return;
    viewSentFor.current = slug;
    splunkInstance('search').sendEvent({
      group: 'profile_visit_online_doctor_recommendation',
      type: 'profile_visit_online_doctor_recommendation-view-inline',
      event: {
        slug,
        variant: 'online-first',
      },
    });
  }, [substituteDoctor?.url]);

  // Don't render if partner key is present or no substitute doctor found
  if (customize.partnerKey || !substituteDoctor?.url) {
    return null;
  }

  return (
    <div id={id} className="flex flex-col space-y-2">
      <SubstituteDoctorSuggestion
        doctor={substituteDoctor}
        groupName={groupName}
        onSelect={element => handleClickDoctorCard({ doctor: substituteDoctor, element })}
      />
      {inPersonLine && (
        <Button block size="sm" className="text-xs" variant="text" onClick={handleClickInPersonLine}>
          ترجیح می‌دهید حضوری بروید؟ {inPersonLine.count} پزشک {groupName ?? ''}
          {inPersonLine.cityName ? ` در ${inPersonLine.cityName}` : ''}، اولین نوبت {inPersonLine.earliest}
        </Button>
      )}
      <Button block size="sm" className="text-xs opacity-70" variant="text" onClick={handleClickMoreDoctors}>
        مشاهده سایر پزشکان آنلاین {expertises?.group_expertises?.[0]?.name} مشابه
      </Button>
    </div>
  );
};

export default SubstituteDoctorsInline;
